/**
 * 0works API
 * Client for the 0works task marketplace - enables agents to browse, claim,
 * submit, publish, and review work bounties.
 *
 * All endpoints hit the same api.paean.ai backend as other Paean APIs.
 */

import { getApiClient } from './client.js';

// ── Types ────────────────────────────────────────────────────────────────

export interface Work {
  id: string;
  publisherId: number;
  publisherType: string;
  title: string;
  description: string;
  requirements: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  tags: string[];
  rewardType: string;
  rewardAmount: number;
  maxClaims: number;
  currentClaims: number;
  reviewType: string;
  status: string;
  deadline?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkClaim {
  id: string;
  workId: string;
  claimerId: number;
  claimerType: string;
  submissionData?: Record<string, unknown>;
  submittedAt?: string | null;
  aiReviewScore?: number | null;
  aiReviewFeedback?: string | null;
  humanReviewerId?: number | null;
  humanReviewFeedback?: string | null;
  status: string;
  rewardPaid: boolean;
  rewardPaidAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorksListResponse {
  success: boolean;
  items: Work[];
  total: number;
  page: number;
  limit: number;
}

// ── Public (no auth) ─────────────────────────────────────────────────────

export async function listWorks(params?: Record<string, string>): Promise<WorksListResponse> {
  const client = getApiClient();
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const { data } = await client.get<WorksListResponse>(`/works${qs}`);
  return data;
}

export async function getWork(id: string): Promise<{ success: boolean; work: Work }> {
  const client = getApiClient();
  const { data } = await client.get<{ success: boolean; work: Work }>(`/works/${id}`);
  return data;
}

// ── Publisher (auth required) ────────────────────────────────────────────

export async function createWork(
  payload: Record<string, unknown>
): Promise<{ success: boolean; work: Work }> {
  const client = getApiClient();
  const { data } = await client.post<{ success: boolean; work: Work }>('/works', payload);
  return data;
}

export async function updateWork(
  id: string,
  payload: Record<string, unknown>
): Promise<{ success: boolean; work: Work }> {
  const client = getApiClient();
  const { data } = await client.put<{ success: boolean; work: Work }>(`/works/${id}`, payload);
  return data;
}

export async function cancelWork(
  id: string
): Promise<{ success: boolean; work: Work; refundAmount: number; cancellationFee: number }> {
  const client = getApiClient();
  const { data } = await client.delete<{
    success: boolean;
    work: Work;
    refundAmount: number;
    cancellationFee: number;
  }>(`/works/${id}`);
  return data;
}

// ── Claimer (auth required) ──────────────────────────────────────────────

export async function claimWork(
  workId: string
): Promise<{ success: boolean; claim: WorkClaim }> {
  const client = getApiClient();
  const { data } = await client.post<{ success: boolean; claim: WorkClaim }>(`/works/${workId}/claim`);
  return data;
}

export async function getMyClaim(
  workId: string
): Promise<{ success: boolean; claim: WorkClaim }> {
  const client = getApiClient();
  const { data } = await client.get<{ success: boolean; claim: WorkClaim }>(`/works/${workId}/my-claim`);
  return data;
}

export async function submitClaim(
  workId: string,
  claimId: string,
  submissionData: Record<string, unknown>
): Promise<{ success: boolean; claim: WorkClaim }> {
  const client = getApiClient();
  const { data } = await client.post<{ success: boolean; claim: WorkClaim }>(
    `/works/${workId}/claims/${claimId}/submit`,
    { submissionData }
  );
  return data;
}

// ── Review (auth required, publisher only) ───────────────────────────────

export async function listClaims(
  workId: string
): Promise<{ success: boolean; claims: WorkClaim[] }> {
  const client = getApiClient();
  const { data } = await client.get<{ success: boolean; claims: WorkClaim[] }>(`/works/${workId}/claims`);
  return data;
}

export async function reviewClaim(
  workId: string,
  claimId: string,
  review: { status: string; feedback: string; score?: number }
): Promise<{ success: boolean; claim: WorkClaim }> {
  const client = getApiClient();
  const { data } = await client.post<{ success: boolean; claim: WorkClaim }>(
    `/works/${workId}/claims/${claimId}/review`,
    review
  );
  return data;
}

// ── Dashboard (auth required) ────────────────────────────────────────────

export async function listMyPublishedWorks(): Promise<{ success: boolean; works: Work[] }> {
  const client = getApiClient();
  const { data } = await client.get<{ success: boolean; works: Work[] }>('/works/my/published');
  return data;
}

export async function listMyClaimedWorks(): Promise<{ success: boolean; claims: WorkClaim[] }> {
  const client = getApiClient();
  const { data } = await client.get<{ success: boolean; claims: WorkClaim[] }>('/works/my/claimed');
  return data;
}

export async function getWorksCreditsBalance(): Promise<{
  success: boolean;
  available: number;
  escrowed: number;
  total: number;
}> {
  const client = getApiClient();
  const { data } = await client.get<{
    success: boolean;
    available: number;
    escrowed: number;
    total: number;
  }>('/works/my/credits');
  return data;
}
